import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {Compbaser} from "../../../components/compbaser";
import {StoreSelectorsService} from "../../../services";
import {IAppStatic, ISceneTemplate} from "../../../interfaces";
import {sceneTemplates} from "../../../../assets/accountTemplates/scene-template-constants";
import {SceneMimeTypes} from "../../editor/scene-blocks/mime-constants";
import {DialogImportSceneComponent} from "../../../components/dialog-import-scene/dialog-import-scene.component";

interface IShowcaseSlide {
    sceneName: string;
    sceneTitle: string;
    folder: string;
    template: ISceneTemplate;
    url: string;
}

/** Big animated showcase card. Cycles through five hand-picked template
 *  scenes with a Ken Burns zoom + crossfade so the page feels alive the
 *  second it loads. Clicking the card opens the same import-scene dialog
 *  the templates page uses — one click and they're in the editor with
 *  this design pre-loaded into their account. */
@Component({
    selector: "app-home-showcase",
    templateUrl: "./home-showcase.component.html",
    styleUrls: ["./home-showcase.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HomeShowcaseComponent extends Compbaser implements OnInit, OnDestroy {
    /** Folders we never feature in the landscape showcase. Anything with
     *  "vertical" in the name was authored for portrait screens and
     *  shows up cropped + ugly in a 21:9 stage. "presentation" folders
     *  are multi-scene playlist templates — a single snapshot can't
     *  represent them, so they're skipped too. Case-insensitive so it
     *  catches "Background vertical", "vertical", "Presentation", future
     *  variants. */
    private static readonly SKIP_FOLDER_PATTERN = /vertical|presentation/i;

    private static readonly ROTATE_MS = 7000;
    /** Window radius around activeIdx that we keep mounted with a real
     *  `src`. Anything outside this window stays as an empty placeholder
     *  until it scrolls into the window, which prevents 200+ snapshot
     *  PNGs from racing the network on first paint. The window also
     *  preloads the slide right before and right after the active one
     *  so the rotation never shows a cold image (unless that single
     *  preload happens to be slower than the 7s rotation interval, in
     *  which case the loader fills the gap). */
    private static readonly PRELOAD_RADIUS = 1;

    slides: IShowcaseSlide[] = [];
    activeIdx = 0;
    isHovered = false;
    appSettings: IAppStatic;
    private timer: any = null;

    /** sceneNames whose `<img>` already fired `load`. Drives the loader
     *  overlay: while a slide's name is NOT in this set, the loader sits
     *  on top of the image. Plain object lookup is fine but a Set is a
     *  smidge nicer with hundreds of entries. */
    private loadedNames = new Set<string>();
    /** sceneNames whose `<img>` fired `error` — snapshot doesn't exist
     *  on S3 yet. We auto-skip past these in `next()` so the user never
     *  sees a broken-image placeholder; if EVERY snapshot in the pool
     *  is broken the rotation just halts on the first one (unlikely). */
    private errorNames = new Set<string>();
    /** sceneNames whose `<img>` we've ever asked the browser to load
     *  (i.e. were in the preload window at least once). Once a name is
     *  in here it stays in here, so as the user advances we accumulate
     *  loaded slides rather than re-fetching when they cycle back. */
    private touchedNames = new Set<string>();

    constructor(
        private storeSelector: StoreSelectorsService,
        protected override dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        super(dialog);
    }

    ngOnInit(): void {
        this.cod(
            this.storeSelector.getAppSettings().subscribe((appSettings: IAppStatic) => {
                this.appSettings = appSettings;
                this.buildSlides();
            }),
        );
        this.startRotation();
    }

    override ngOnDestroy(): void {
        this.stopRotation();
        super.ngOnDestroy();
    }

    /** Build the full eligible pool — every Player-mime template scene
     *  whose folder isn't a vertical/portrait category. Shuffled in
     *  place so the same user gets fresh sequencing on each visit. */
    private buildSlides(): void {
        if (!this.appSettings) return;
        const candidates: IShowcaseSlide[] = [];
        for (const sceneName in sceneTemplates) {
            const tpl = sceneTemplates[sceneName];
            if (!tpl) continue;
            if (tpl[3] !== SceneMimeTypes.Player) continue;
            const meta = tpl[4];
            if (!meta || !meta.folder) continue;
            if (HomeShowcaseComponent.SKIP_FOLDER_PATTERN.test(meta.folder)) continue;
            const url = `${this.appSettings.cloudResourcesS3}/business1000/resources/scenes_snaps/${sceneName}.png`;
            candidates.push({
                sceneName,
                sceneTitle: meta.name,
                folder: meta.folder,
                url,
                template: {
                    // ISceneTemplate declares these three as strings even
                    // though SceneTemplateConfig stores them as numbers —
                    // coerce here so the dialog (which interpolates them
                    // into URLs) gets a stable string the way templates
                    // page passes them too.
                    nativeId: String(tpl[2]),
                    sceneTitle: meta.name,
                    sceneId: String(tpl[2]),
                    sceneName,
                    height: "1080",
                    width: "1920",
                    mimeType: tpl[3],
                    businessId: String(tpl[0]),
                    url,
                    folder: meta.folder,
                    childSceneIds: meta.sceneIds,
                },
            });
        }
        // Fisher-Yates — same shuffle pattern templates uses. We keep
        // the FULL pool (no slice) so the user rotates through every
        // category over time, but only the slides in the preload window
        // around activeIdx actually fetch their image.
        for (let i = candidates.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
        }
        this.slides = candidates;
        this.activeIdx = 0;
        this.loadedNames.clear();
        this.errorNames.clear();
        this.touchedNames.clear();
        // Mark the initial preload window so the first paint actually
        // requests an image (otherwise we'd just sit on a loader forever).
        this.markWindowTouched();
        this.cdr.markForCheck();
    }

    /** Add every slide inside [activeIdx ± PRELOAD_RADIUS] (with wrap)
     *  to `touchedNames`. Names stay touched forever — once an image
     *  has been requested we never un-request it, the browser will keep
     *  it cached and we want the rotation to feel instant on revisit. */
    private markWindowTouched(): void {
        const n = this.slides.length;
        if (!n) return;
        for (let d = -HomeShowcaseComponent.PRELOAD_RADIUS; d <= HomeShowcaseComponent.PRELOAD_RADIUS; d++) {
            const idx = ((this.activeIdx + d) % n + n) % n;
            const slide = this.slides[idx];
            if (slide) this.touchedNames.add(slide.sceneName);
        }
    }

    /** Template-side helper — should this slide have its `src` set? */
    shouldLoad(slide: IShowcaseSlide): boolean {
        return this.touchedNames.has(slide.sceneName);
    }

    isLoaded(slide: IShowcaseSlide): boolean {
        return this.loadedNames.has(slide.sceneName);
    }

    /** `(load)` handler from the template `<img>`. Marks the snapshot
     *  as ready so the loader overlay can fade out. */
    onImgLoad(slide: IShowcaseSlide): void {
        this.loadedNames.add(slide.sceneName);
        this.cdr.markForCheck();
    }

    /** `(error)` handler — snapshot doesn't exist on S3. We mark it
     *  errored, drop the loader, and if THIS is the active slide we
     *  silently skip forward so the user never sees the broken state. */
    onImgError(slide: IShowcaseSlide): void {
        this.errorNames.add(slide.sceneName);
        this.loadedNames.add(slide.sceneName); // stop the loader overlay
        if (this.slides[this.activeIdx]?.sceneName === slide.sceneName) {
            // Advance once. If we land on another errored one the next
            // tick of the rotation will handle it.
            this.next();
        }
        this.cdr.markForCheck();
    }

    /** Auto-rotate timer. NgZone.runOutsideAngular keeps the polling
     *  off CD until we actually swap a slide. */
    private startRotation(): void {
        this.stopRotation();
        this.zone.runOutsideAngular(() => {
            this.timer = setInterval(() => {
                if (this.isHovered || this.slides.length === 0) return;
                this.zone.run(() => {
                    this.next();
                });
            }, HomeShowcaseComponent.ROTATE_MS);
        });
    }

    private stopRotation(): void {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    next(): void {
        if (!this.slides.length) return;
        this.activeIdx = (this.activeIdx + 1) % this.slides.length;
        this.markWindowTouched();
        this.cdr.markForCheck();
    }

    prev(): void {
        if (!this.slides.length) return;
        this.activeIdx = (this.activeIdx - 1 + this.slides.length) % this.slides.length;
        this.markWindowTouched();
        this.cdr.markForCheck();
    }

    pickIdx(i: number): void {
        if (i < 0 || i >= this.slides.length) return;
        this.activeIdx = i;
        this.markWindowTouched();
        this.cdr.markForCheck();
    }

    onMouseEnter(): void {
        this.isHovered = true;
    }

    onMouseLeave(): void {
        this.isHovered = false;
    }

    /** Same dialog the templates page opens — one click to fork the
     *  design into the user's account. */
    useThisTemplate(): void {
        const slide = this.slides[this.activeIdx];
        if (!slide) return;
        this.dialog.open(DialogImportSceneComponent, {
            width: "600px",
            data: {
                template: slide.template,
                showDownloadingCancel: true,
                routerAfterImport: true,
            },
            disableClose: true,
        });
    }
}
